/**
 * Asking for the sign-in link again.
 *
 * Mail is late more often than it is lost, and a person staring at "check
 * your email" needs a way to ask again that is not "reload and retype". But
 * "send again" is also the easiest button on the site to abuse, so it is
 * paced: a cool-down after each send, and a small number of repeats per
 * address per visit. The backend keeps its own count per address and per
 * machine (functions/src/auth/signInLink.ts) and quietly stops sending past
 * it; the numbers here sit under that limit so an honest person never
 * reaches the silent one.
 */

export const RESEND_COOLDOWN_SECONDS = 60;
/** Repeats after the first send. With the first, four requests per visit —
 *  one under the backend's five per address per fifteen minutes. */
export const MAX_RESENDS = 3;
/** How long the count is remembered for an address, matching the backend's window. */
export const RESEND_WINDOW_MINUTES = 15;

export interface ResendRecord {
  /** Sends so far, the first included. */
  sends: number;
  /** When the last one went, epoch milliseconds. */
  lastSentAt: number;
}

export type ResendState =
  | { kind: 'wait'; seconds: number }
  | { kind: 'ready'; remaining: number }
  | { kind: 'exhausted' };

export function resendState(record: ResendRecord, now: number = Date.now()): ResendState {
  const resendsUsed = Math.max(0, record.sends - 1);
  if (resendsUsed >= MAX_RESENDS) return { kind: 'exhausted' };
  const elapsed = Math.floor((now - record.lastSentAt) / 1000);
  const wait = RESEND_COOLDOWN_SECONDS - elapsed;
  if (wait > 0) return { kind: 'wait', seconds: wait };
  return { kind: 'ready', remaining: MAX_RESENDS - resendsUsed };
}

const KEY = 'standingdocs.signin.resend';

/** The record for this address, if one was kept and is still within the window. */
export function readRecord(email: string, now: number = Date.now()): ResendRecord | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { email?: string; sends?: number; lastSentAt?: number };
    if (parsed.email !== email.toLowerCase() || typeof parsed.sends !== 'number' || typeof parsed.lastSentAt !== 'number') return null;
    if (now - parsed.lastSentAt > RESEND_WINDOW_MINUTES * 60 * 1000) return null;
    return { sends: parsed.sends, lastSentAt: parsed.lastSentAt };
  } catch {
    return null;
  }
}

export function writeRecord(email: string, record: ResendRecord): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ email: email.toLowerCase(), ...record }));
  } catch {
    // A private window or blocked storage: the count lives in memory only,
    // and the backend's own limit still holds.
  }
}
